import { ProductItem } from '@/lib/types/api';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ButtonGhost } from '../ui/Buttons/ButtonGhost/ButtonGhost';
import Drawer from '../ui/Modals/Drawer/Drawer';
import { Input } from './Input';
import { SelectCountry } from './SelectCountry';
import { InputTerms } from './InputTerms';
import { saveBuylead } from '@/lib/repository/crm/CRMRepository';
import { SuccessCheck } from './Success/SuccessCheck';
import {
  getLocaleFormat,
  hasPhonePrefix,
} from '@/lib/data-transformer/gindumacUtils';
import { useRouter } from 'next/router';
import {
  getGTMProductData,
  pushDataLayer,
} from '@/lib/utilities/analytics/gtm';
import { bingUET } from '@/lib/utilities/analytics/bing';
import useUserMetadata from '@/lib/hooks/useUserData';
import { useUser } from '@auth0/nextjs-auth0/client';
import COUNTRY_NAMES from '@/lib/data/country-names.json';
import { LinkEditMetadata } from '../ui/Links/LinkEditMetadata';

function MakeCounterOffer({ product }: { product: ProductItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('products');
  const { locale } = useRouter();
  type FormInputs = {
    'counter-offer_first-name': string;
    'counter-offer_last-name': string;
    'counter-offer_city': string;
    'counter-offer_email': string;
    'counter-offer_company': string;
    'counter-offer_phone-prefix': string;
    'counter-offer_phone': string;
    'counter-offer_country': string;
    'counter-offer_terms': boolean;
    'counter-offer_price': string;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    watch,
    reset,
  } = useForm<FormInputs>({
    mode: 'onBlur',
  });
  const localeFormat = getLocaleFormat(locale ?? 'de-DE');

  async function submit(data: FormInputs) {
    console.log(data);
    try {
      await saveBuylead({
        'first-name': data['counter-offer_first-name'],
        'last-name': data['counter-offer_last-name'],
        city: data['counter-offer_city'],
        email: data['counter-offer_email'],
        company: data['counter-offer_company'],
        'phone-prefix': data['counter-offer_phone-prefix'],
        'phone-number': data['counter-offer_phone'],
        country: data['counter-offer_country'],
        'platform-type': 'Counteroffer',
        'platform-name': 'GINDUMAC Website',
        offer: data['counter-offer_price'].replace(/\.|,|\s/g, ''),
        sku: product.sku,
        currency: localeFormat.currency_ISO,
      });
      tracking();
    } catch (error) {
      console.log('errror', error);
    }
  }

  function tracking() {
    bingUET('Make a counteroffer');
    const formType = 'Make a counteroffer';
    pushDataLayer('formSubmission', {
      formType,
    });
    pushDataLayer('begin_checkout', {
      formType,
      ecommerce: {
        value: product.price,
        currency: product.currency,
        items: [getGTMProductData(product)],
      },
    });
  }

  const { metadata } = useUserMetadata();
  const { user } = useUser();
  const [hasPrefixInput, setHasPrefixInput] = useState(false);
  useEffect(() => {
    function handleReset() {
      const phone = metadata?.phone ?? metadata?.companyPhone;
      setHasPrefixInput(!hasPhonePrefix(phone));
      reset({
        'counter-offer_first-name': metadata?.firstName,
        'counter-offer_last-name': metadata?.lastName,
        'counter-offer_company': metadata?.companyName,
        'counter-offer_city': metadata?.city ?? metadata?.companyCity,
        'counter-offer_email': user?.email ?? metadata?.companyEmail,
        'counter-offer_phone': phone,
        'counter-offer_country': COUNTRY_NAMES.find(
          (e) => e[1] === (metadata?.country ?? metadata?.companyCountry)
        )?.[0],
      });
    }
    // run handleReset after one sec
    const timer = setTimeout(() => handleReset(), 1000);
    return () => clearTimeout(timer);
  }, [metadata, reset, isOpen, user?.email, setHasPrefixInput]);

  return (
    <>
      <div
        onClick={() => setIsOpen(true)}
        data-testid='btn-open-make-counter-offer'
      >
        <ButtonGhost>{t('DETAIL_ASIDE_BTN_COUNTEROFFER')}</ButtonGhost>
      </div>
      <Drawer hasLogo isOpen={isOpen} setIsOpen={setIsOpen} size='full'>
        <hr />
        <div
          data-testid='counter-offer-form'
          className='flex min-h-[80vh] items-center justify-center'
        >
          {!isSubmitSuccessful ? (
            <form
              className='mx-auto max-w-xl'
              onSubmit={handleSubmit(submit)}
              data-testid='get-counter-offer_form'
            >
              <div className='space-y-2'>
                <h1 className='my-6 text-center'>
                  {t('DETAIL_COUNTEROFFER_HEADER')}
                </h1>
                <LinkEditMetadata />

                <div className='flex space-x-2'>
                  <div className='w-1/2'>
                    <Input<FormInputs>
                      name='counter-offer_first-name'
                      register={register}
                      errors={errors}
                      placeholder={t('FORM_PLACEHOLDER_FIRSTNAME')}
                      validation={{
                        required: true,
                        maxLength: 50,
                      }}
                    />
                  </div>
                  <div className='w-1/2'>
                    <Input<FormInputs>
                      name='counter-offer_last-name'
                      register={register}
                      errors={errors}
                      placeholder={t('FORM_PLACEHOLDER_LASTNAME')}
                      validation={{
                        required: true,
                        maxLength: 50,
                      }}
                    />
                  </div>
                </div>
                <Input<FormInputs>
                  name='counter-offer_company'
                  register={register}
                  errors={errors}
                  placeholder={t('FORM_PLACEHOLDER_COMPANY')}
                  validation={{
                    required: true,
                    maxLength: 100,
                  }}
                />
                <Input<FormInputs>
                  type='email'
                  name='counter-offer_email'
                  register={register}
                  errors={errors}
                  placeholder={t('FORM_PLACEHOLDER_EMAIL')}
                  validation={{
                    required: true,
                    maxLength: 100,
                  }}
                />
                <div className='flex space-x-2'>
                  {hasPrefixInput && (
                    <div className='w-1/2'>
                      <SelectCountry
                        type='phoneCode'
                        name='counter-offer_phone-prefix'
                        register={register}
                        errors={errors}
                        validation={{
                          required: true,
                        }}
                      />
                    </div>
                  )}

                  <div className={hasPrefixInput ? 'w-1/2' : 'w-full'}>
                    <Input<FormInputs>
                      type={'text'}
                      name='counter-offer_phone'
                      register={register}
                      errors={errors}
                      placeholder={t('FORM_PLACEHOLDER_PHONE')}
                      validation={{
                        required: true,
                        maxLength: 20,
                      }}
                    />
                  </div>
                </div>
                <Input<FormInputs>
                  name='counter-offer_city'
                  register={register}
                  errors={errors}
                  placeholder={t('FORM_PLACEHOLDER_CITY').toUpperCase()}
                  validation={{
                    required: true,
                    maxLength: 100,
                  }}
                />
                <SelectCountry
                  type='country'
                  name='counter-offer_country'
                  register={register}
                  errors={errors}
                  validation={{
                    required: true,
                  }}
                />
                <div>
                  <h5 className='h3 pb-2 text-center'>
                    {t('DETAIL_COUNTEROFFER_SUBHEADER')}
                  </h5>
                </div>
                <Input<FormInputs>
                  name='counter-offer_price'
                  register={register}
                  errors={errors}
                  validation={{
                    required: true,
                    pattern: {
                      // regex for numbers and dots and commas
                      value: /^[0-9.,]+$/,
                      message: t('products:FORM_ERROR_PRICE_EMPTY'),
                    },
                    setValueAs: (v: string) => {
                      const r = v
                        .replace(/[^\d]/g, '')
                        .replace(
                          /(\d)(?=(\d{3})+(?!\d))/g,
                          '$1' + localeFormat.thousand
                        );
                      return r === '' ? ' ' : r;
                    },
                  }}
                  value={watch('counter-offer_price')}
                  sufix={localeFormat.currency}
                />
                <InputTerms<FormInputs>
                  name='counter-offer_terms'
                  register={register}
                  errors={errors}
                />
              </div>
              <div className='flex w-full items-center justify-center lg:justify-end'>
                <button
                  type='submit'
                  className='mb-12 mt-4 w-full max-w-xs rounded bg-brand-500 px-8 py-4 text-center uppercase text-white hover:bg-brand-400'
                  disabled={isSubmitting}
                >
                  {t('DETAIL_MODALQUOTE_BTN_SEND')}
                </button>
              </div>
            </form>
          ) : (
            <SuccessCheck />
          )}
        </div>
      </Drawer>
    </>
  );
}

export function FormTitle({ title }: { title: string }) {
  return (
    <div>
      <p className='text-center text-muted-500'>{title}</p>
    </div>
  );
}

export default MakeCounterOffer;
